import { useEffect, useState, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';

import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { SeveritySelectTrigger } from '@/components/ui/severity-select-trigger';
import { Switch } from '@/components/ui/switch';
import {
  useScopeChangeAction,
  useUnsavedChangeActions,
} from '@/hooks/useScopeChangeHooks';
import type { slackSettings } from '@kirin/api-client/contracts';
import { Severity } from '@kirin/api-client/contracts/shared/severity';

const SLACK_SEVERITY_OPTIONS = [
  { value: Severity.Low, label: 'Low - All security findings' },
  { value: Severity.Medium, label: 'Medium - Moderate and above' },
  { value: Severity.High, label: 'High - High and critical only' },
  { value: Severity.Critical, label: 'Critical - Critical only' },
] as const;

type FormData = slackSettings.UpsertSlackSettingsBody;

// Defensive coercion: API may return an unknown severity string; fall back to
// High so the form always lands on a valid Severity enum value.
function toSeverity(value: string | undefined | null): Severity {
  if (!value) {
    return Severity.High;
  }

  const normalized = value.toLowerCase();
  if (Object.values(Severity).includes(normalized as Severity)) {
    return normalized as Severity;
  }
  return Severity.High;
}

export interface ConnectSlackModalProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  scopeId: string;
  settings: slackSettings.SlackSettingsResponse | null | undefined;
  isLoading: boolean;
  onSave: (
    data: slackSettings.UpsertSlackSettingsBody
  ) => Promise<slackSettings.UpsertSlackSettingsResponse>;
  onRequestDisconnect: () => void;
  /** When true, all write actions (save, delete) are disabled. */
  readOnly?: boolean;
}

export function ConnectSlackModal(props: ConnectSlackModalProps): ReactElement {
  const {
    open,
    onOpenChange,
    scopeId: scopeIdProp,
    settings,
    isLoading,
    onSave,
    onRequestDisconnect,
    readOnly = false,
  } = props;

  const [isProcessing, setIsProcessing] = useState(false);

  const { markDirty, markClean } = useUnsavedChangeActions();
  const { changeScopeTo } = useScopeChangeAction();
  const scopeId = `slack-integration-${scopeIdProp}`;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isDirty, errors },
  } = useForm<FormData>({
    defaultValues: {
      enabled: false,
      webhookUrl: '',
      minRiskLevel: Severity.High,
      sendTestMessage: false,
    },
  });

  useEffect(() => {
    if (settings) {
      reset({
        enabled: settings.enabled,
        webhookUrl: settings.webhookUrl,
        minRiskLevel: toSeverity(settings.minRiskLevel),
        sendTestMessage: false,
      });
    }
  }, [settings, reset]);

  useEffect(() => {
    if (isDirty) {
      markDirty(scopeId);
    } else {
      markClean(scopeId);
    }
    return () => {
      markClean(scopeId);
    };
  }, [isDirty, markDirty, markClean, scopeId]);

  const enabled = watch('enabled');
  const webhookUrl = watch('webhookUrl');
  const currentMinRiskLevel = watch('minRiskLevel');

  const handleSave = async (data: FormData): Promise<void> => {
    try {
      setIsProcessing(true);
      const result = await onSave(data);
      reset(
        {
          enabled: result.enabled,
          webhookUrl: result.webhookUrl,
          minRiskLevel: toSeverity(result.minRiskLevel),
          sendTestMessage: false,
        },
        { keepValues: true }
      );
      markClean(scopeId);
    } catch {
      // Container surfaces the toast and re-throws; modal preserves form state.
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveAndTest = handleSubmit(async data => {
    await handleSave({ ...data, sendTestMessage: true });
  });

  const handleOpenChange = (nextOpen: boolean): void => {
    if (nextOpen) {
      return;
    }
    if (isProcessing) return;
    if (!isDirty) {
      onOpenChange(false);
      return;
    }
    changeScopeTo({
      from: scopeId,
      id: 'close-slack-integration-modal',
      title: 'Unsaved changes',
      body: 'You have unsaved changes that will be lost.',
      cancelLabel: 'Keep Editing',
      discardLabel: 'Discard',
      run: () => {
        onOpenChange(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col p-0 gap-0 text-foreground">
        <DialogHeader className="shrink-0 px-6 py-4 border-b">
          <DialogTitle className="text-primary">
            Slack Notifications
          </DialogTitle>
          <DialogDescription>
            Configure Slack notifications for security scan alerts
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {isLoading ? (
            <div className="text-sm text-muted-foreground">
              Loading settings...
            </div>
          ) : (
            <form
              id="slack-config-form"
              onSubmit={handleSubmit(handleSave)}
              className="space-y-6"
            >
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="enabled">Enable Notifications</Label>
                  <div className="text-sm text-muted-foreground">
                    Send Slack notifications for security scan alerts
                  </div>
                </div>
                <Switch
                  id="enabled"
                  checked={enabled}
                  autoFocus
                  disabled={readOnly}
                  onCheckedChange={checked =>
                    setValue('enabled', checked, { shouldDirty: true })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="webhookUrl">
                  Slack Webhook URL <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="webhookUrl"
                  type="text"
                  placeholder="https://hooks.slack.com/services/..."
                  {...register('webhookUrl', {
                    required: 'Webhook URL is required',
                    // Mirrors upsertSlackSettingsBodySchema in packages/api-client/src/contracts/slack-settings.ts
                    pattern: {
                      value: /^https:\/\/hooks\.slack\.com\/.+/,
                      message:
                        'Must be a valid Slack webhook URL (starts with https://hooks.slack.com/)',
                    },
                  })}
                  disabled={!enabled}
                  aria-invalid={errors.webhookUrl ? true : undefined}
                  aria-describedby={
                    errors.webhookUrl ? 'webhookUrl-error' : undefined
                  }
                />
                {errors.webhookUrl && (
                  <p
                    id="webhookUrl-error"
                    role="alert"
                    className="text-sm text-destructive"
                  >
                    {errors.webhookUrl.message}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">
                  Get your webhook URL from Slack&apos;s{' '}
                  <a
                    href="https://api.slack.com/messaging/webhooks"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Incoming Webhooks
                  </a>{' '}
                  configuration
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="minRiskLevel">Minimum Severity Level</Label>
                <Select
                  key={currentMinRiskLevel}
                  value={currentMinRiskLevel || Severity.High}
                  onValueChange={value =>
                    setValue('minRiskLevel', value as Severity, {
                      shouldDirty: true,
                    })
                  }
                  disabled={!enabled}
                >
                  <SeveritySelectTrigger id="minRiskLevel">
                    <SelectValue />
                  </SeveritySelectTrigger>
                  <SelectContent>
                    {SLACK_SEVERITY_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        <SeverityBadge
                          riskLevel={option.value}
                          showIcon
                          label={option.label}
                          className="bg-transparent border-0 p-0"
                        />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Only send notifications for scans that meet or exceed this
                  severity level
                </p>
              </div>
            </form>
          )}
        </div>

        <DialogFooter className="shrink-0 px-6 py-4 border-t gap-2 sm:flex-row sm:justify-end">
          {settings && (
            <Button
              type="button"
              variant="destructive"
              className="mr-auto"
              onClick={onRequestDisconnect}
              disabled={isProcessing || readOnly}
            >
              Delete Settings
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={() => handleOpenChange(false)}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleSaveAndTest}
            disabled={!enabled || !webhookUrl || isProcessing || readOnly}
          >
            {isProcessing ? 'Sending...' : 'Save & Test'}
          </Button>
          <Button
            type="submit"
            form="slack-config-form"
            disabled={!webhookUrl || !isDirty || isProcessing || readOnly}
          >
            {isProcessing ? 'Saving...' : 'Save Settings'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

ConnectSlackModal.displayName = 'ConnectSlackModal';
